import React, { useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import './AdminPage.css';

function DBManagement() {
  const [dbFiles, setDbFiles] = useState([]);
  const [selectedDb, setSelectedDb] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [records, setRecords] = useState([]);
  const [editing, setEditing] = useState({});
  const [columns, setColumns] = useState([]);
  const [orgOptions, setOrgOptions] = useState([]);
  const [empOptions, setEmpOptions] = useState([]);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState('');
  const [selectedEmpFilter, setSelectedEmpFilter] = useState('');

  useEffect(() => {
    fetchDbFiles();
  }, []);

  const fetchDbFiles = async () => {
    try {
      const files = await apiFetch('/admin/db-files', null);
      setDbFiles(files);
    } catch (e) {
      console.error('Failed to list DB files', e);
      setDbFiles([]);
    }
  };

  const handleSelectDb = async (f) => {
    setSelectedDb(f);
    setSelectedTable(null);
    setTables([]);
    setRecords([]);
    try {
      const tbls = await apiFetch(`/admin/db-tables?db=${encodeURIComponent(f)}`, null);
      setTables(tbls);
    } catch (e) {
      console.error('Failed to list tables', e);
      setTables([]);
    }
  };

  const handleSelectTable = async (t) => {
    setSelectedTable(t);
    setRecords([]);
    setColumns([]);
    setOrgOptions([]);
    setEmpOptions([]);
    setSelectedOrgFilter('');
    setSelectedEmpFilter('');
    try {
      // fetch columns first
      const cols = await apiFetch(`/admin/db-columns?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(t)}`, null);
      setColumns(cols || []);

      // fetch records (no filters)
      const recs = await apiFetch(`/admin/db-records?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(t)}&limit=200`, null);
      setRecords(recs || []);

      // If organization_id exists, fetch distinct org ids and map to names
      if ((cols || []).includes('organization_id')) {
        try {
          const orgIds = await apiFetch(`/admin/db-distinct?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(t)}&column=organization_id`, null);
          if (Array.isArray(orgIds) && orgIds.length) {
            const idsParam = orgIds.join(',');
            const orgRows = await apiFetch(`/admin/db-lookup?db=${encodeURIComponent(selectedDb)}&table=organizations&key=id&ids=${encodeURIComponent(idsParam)}&cols=id,name`, null);
            const opts = orgRows.map(r => ({ id: r.id, label: `${r.id} - ${r.name || ''}` }));
            setOrgOptions(opts);
          }
        } catch (e) {
          console.warn('Org lookup failed', e);
        }
      }

      // If employee_id exists, fetch distinct emp ids and map to employee names
      if ((cols || []).includes('employee_id')) {
        try {
          const empIds = await apiFetch(`/admin/db-distinct?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(t)}&column=employee_id`, null);
          if (Array.isArray(empIds) && empIds.length) {
            const idsParam = empIds.join(',');
            const empRows = await apiFetch(`/admin/db-lookup?db=${encodeURIComponent(selectedDb)}&table=employees&key=id&ids=${encodeURIComponent(idsParam)}&cols=id,first_name,last_name`, null);
            const opts = empRows.map(r => ({ id: r.id, label: `${r.id} - ${[r.first_name, r.last_name].filter(Boolean).join(' ')}` }));
            setEmpOptions(opts);
          }
        } catch (e) {
          console.warn('Employee lookup failed', e);
        }
      }
    } catch (e) {
      console.error('Failed to list records', e);
      setRecords([]);
    }
  };

  const fetchRecordsWithFilters = async (orgId, empId) => {
    try {
      let url = `/admin/db-records?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(selectedTable)}&limit=200`;
      if (orgId) url += `&filter_col=organization_id&filter_val=${encodeURIComponent(orgId)}`;
      if (empId) url += `${orgId ? '&' : '&'}filter_col=employee_id&filter_val=${encodeURIComponent(empId)}`;
      // Note: backend only supports single filter_col currently; prefer org or emp
      // prefer employee filter if provided
      if (empId) {
        url = `/admin/db-records?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(selectedTable)}&filter_col=employee_id&filter_val=${encodeURIComponent(empId)}&limit=200`;
      } else if (orgId) {
        url = `/admin/db-records?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(selectedTable)}&filter_col=organization_id&filter_val=${encodeURIComponent(orgId)}&limit=200`;
      }
      const recs = await apiFetch(url, null);
      setRecords(recs || []);
    } catch (e) {
      console.error('Failed to list records with filters', e);
      setRecords([]);
    }
  };

  const startEdit = (rowIndex, col) => {
    setEditing({ rowIndex, col, value: records[rowIndex][col] });
  };

  const applyEdit = async () => {
    const { rowIndex, col, value } = editing;
    const row = records[rowIndex];
    // Build payload with PK included
    try {
      const payload = { ...{ [col]: value }, ...{} };
      // include id or primary key field; we assume 'id' exists
      if (row.id !== undefined) payload.id = row.id;
      else {
        // fallback: include all original columns so backend can infer pk
        Object.assign(payload, row);
      }

      await apiFetch(`/admin/db-record?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(selectedTable)}`, null, {
        method: 'PUT',
        body: payload,
      });

      // Update local copy
      const updated = [...records];
      updated[rowIndex] = { ...updated[rowIndex], [col]: value };
      setRecords(updated);
      setEditing({});
    } catch (e) {
      console.error('Failed to update record', e);
      alert('Update failed: ' + e.message);
    }
  };

  const handleDelete = async (rowIndex) => {
    const row = records[rowIndex];
    const pk = row.id || row[Object.keys(row)[0]];
    if (!window.confirm('Delete this record?')) return;
    try {
      await apiFetch(`/admin/db-record?db=${encodeURIComponent(selectedDb)}&table=${encodeURIComponent(selectedTable)}&pk=${encodeURIComponent(pk)}`, null, { method: 'DELETE' });
      const updated = records.filter((_, i) => i !== rowIndex);
      setRecords(updated);
    } catch (e) {
      console.error('Delete failed', e);
      alert('Delete failed: ' + e.message);
    }
  };

  return (
    <div className="ap-admin container-fluid py-3">
      <h2 className="ap-section-title h5">DB Management</h2>

      <div className="row g-3">
        <div className="col-md-3">
          <div className="ap-card p-2">
            <h4 className="h6">Databases</h4>
            <ul className="list-unstyled small">
              {dbFiles.map(f => (
                <li key={f}>
                  <button className={`ap-link ${selectedDb === f ? 'text-primary' : ''}`} onClick={() => handleSelectDb(f)}>{f}</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="col-md-3">
          <div className="ap-card p-2">
            <h4 className="h6">Tables</h4>
            <ul className="list-unstyled small">
              {tables.map(t => (
                <li key={t}><button className={`ap-link ${selectedTable === t ? 'text-primary' : ''}`} onClick={() => handleSelectTable(t)}>{t}</button></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <div className="ap-card p-2">
            <h4 className="h6">Records {selectedTable ? ` — ${selectedTable}` : ''}</h4>
            {/* Filters */}
            <div style={{display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center'}}>
              {orgOptions.length > 0 && (
                <div>
                  <label className="ap-form-label small">Organization</label>
                  <select className="ap-form-select" value={selectedOrgFilter} onChange={e => { setSelectedOrgFilter(e.target.value); fetchRecordsWithFilters(e.target.value, selectedEmpFilter); }}>
                    <option value="">All</option>
                    {orgOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
              )}
              {empOptions.length > 0 && (
                <div>
                  <label className="ap-form-label small">Employee</label>
                  <select className="ap-form-select" value={selectedEmpFilter} onChange={e => { setSelectedEmpFilter(e.target.value); fetchRecordsWithFilters(selectedOrgFilter, e.target.value); }}>
                    <option value="">All</option>
                    {empOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            {records.length === 0 ? (
              <div className="ap-empty-state">No records to show.</div>
            ) : (
              <div className="table-responsive">
                <table className="ap-table mb-0">
                  <thead>
                    <tr>
                      {Object.keys(records[0]).map(col => <th key={col}>{col}</th>)}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r, ri) => (
                      <tr key={ri}>
                        {Object.keys(r).map(col => (
                          <td key={col} onDoubleClick={() => startEdit(ri, col)}>
                            {editing.rowIndex === ri && editing.col === col ? (
                              <div style={{display:'flex', gap:8}}>
                                <input autoFocus value={editing.value} onChange={e => setEditing(prev => ({ ...prev, value: e.target.value }))} />
                                <button className="ap-btn ap-btn-primary" onClick={applyEdit}>Save</button>
                                <button className="ap-btn ap-btn-secondary" onClick={() => setEditing({})}>Cancel</button>
                              </div>
                            ) : (
                              String(r[col] === null ? '' : r[col])
                            )}
                          </td>
                        ))}
                        <td>
                          <button className="ap-icon-btn ap-icon-danger" onClick={() => handleDelete(ri)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DBManagement;
